import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, BookOpen, MessageSquare, Loader2 } from 'lucide-react';
import { useProperty } from '../../contexts/PropertyContext';
import { apiService } from '../../services/api';
import { API_CONFIG } from '../../config/api';
import { Button, Input } from '../shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SearchResult {
  id: string;
  label: string;
  sublabel?: string;
  category: 'guests' | 'knowledge-base' | 'sessions';
  route: string;
}

interface GuestProfileResult {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  segment?: string;
  stayStatus?: string;
}

interface KBEntityResult {
  id: string;
  title: string;
  category: string;
}

interface SessionResult {
  sessionId: string;
  status: string;
  guest?: {
    name?: string;
    email?: string;
  };
}

// ---------------------------------------------------------------------------
// Category display config
// ---------------------------------------------------------------------------

const CATEGORY_CONFIG = {
  guests: { label: 'GUESTS', Icon: User },
  'knowledge-base': { label: 'KNOWLEDGE BASE', Icon: BookOpen },
  sessions: { label: 'SESSIONS', Icon: MessageSquare },
} as const;

const CATEGORY_ORDER: SearchResult['category'][] = ['guests', 'knowledge-base', 'sessions'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GlobalSearch() {
  const navigate = useNavigate();
  const { selectedProperty } = useProperty();
  const propertyId = selectedProperty?.id;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // -----------------------------------------------------------------------
  // Search logic
  // -----------------------------------------------------------------------

  const performSearch = useCallback(async (term: string) => {
    if (!propertyId || term.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const searchResults: SearchResult[] = [];

      // Fire all searches in parallel
      const [guestRes, kbRes, sessionRes] = await Promise.allSettled([
        // Guest profiles — backend supports ?search= natively
        apiService.get<{ profiles: GuestProfileResult[] }>(
          `${API_CONFIG.endpoints.guestProfiles.list(propertyId)}?search=${encodeURIComponent(term)}&limit=5&tab=all`
        ),
        // KB entities — fetch all, filter client-side (no server-side search)
        apiService.get<{ items: KBEntityResult[] }>(
          API_CONFIG.endpoints.knowledgeBase.list(propertyId)
        ),
        // Active sessions — fetch all, filter client-side
        apiService.get<{ sessions: SessionResult[] }>(
          API_CONFIG.endpoints.sessions.active(propertyId)
        ),
      ]);

      // Process guest results
      if (guestRes.status === 'fulfilled' && guestRes.value.profiles) {
        for (const guest of guestRes.value.profiles.slice(0, 5)) {
          const name = [guest.firstName, guest.lastName].filter(Boolean).join(' ') || 'Unknown Guest';
          const parts: string[] = [];
          if (guest.email) parts.push(guest.email);
          if (guest.stayStatus) parts.push(guest.stayStatus.replace(/_/g, ' '));

          searchResults.push({
            id: `guest-${guest.id}`,
            label: name,
            sublabel: parts.join(' \u2014 ') || undefined,
            category: 'guests',
            route: `/properties/${propertyId}/guests?profile=${guest.id}`,
          });
        }
      }

      // Process KB results (client-side filter)
      if (kbRes.status === 'fulfilled' && kbRes.value.items) {
        const lowerTerm = term.toLowerCase();
        const matched = kbRes.value.items
          .filter((item) => item.title?.toLowerCase().includes(lowerTerm))
          .slice(0, 5);

        for (const item of matched) {
          searchResults.push({
            id: `kb-${item.id}`,
            label: item.title,
            sublabel: item.category?.replace(/_/g, ' '),
            category: 'knowledge-base',
            route: `/properties/${propertyId}/knowledge-base?entity=${item.id}`,
          });
        }
      }

      // Process session results (client-side filter)
      if (sessionRes.status === 'fulfilled' && sessionRes.value.sessions) {
        const lowerTerm = term.toLowerCase();
        const matched = sessionRes.value.sessions
          .filter((s) => {
            const guestName = s.guest?.name?.toLowerCase() ?? '';
            const guestEmail = s.guest?.email?.toLowerCase() ?? '';
            const id = s.sessionId.toLowerCase();
            return guestName.includes(lowerTerm) || guestEmail.includes(lowerTerm) || id.includes(lowerTerm);
          })
          .slice(0, 5);

        for (const session of matched) {
          const name = session.guest?.name || 'Anonymous';
          const statusLabel = session.status.replace(/_/g, ' ');

          searchResults.push({
            id: `session-${session.sessionId}`,
            label: `Session #${session.sessionId.slice(0, 8)}`,
            sublabel: `${name} \u2014 ${statusLabel}`,
            category: 'sessions',
            route: `/properties/${propertyId}/sessions`,
          });
        }
      }

      setResults(searchResults);
    } catch {
      // Silently fail — keep results empty
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.length < 2) {
      setResults([]);
      setIsLoading(false);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    setIsOpen(true);

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  // -----------------------------------------------------------------------
  // Global Cmd+K / Ctrl+K shortcut
  // -----------------------------------------------------------------------

  useEffect(() => {
    function handleGlobalKeydown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleGlobalKeydown);
    return () => document.removeEventListener('keydown', handleGlobalKeydown);
  }, []);

  // -----------------------------------------------------------------------
  // Click outside
  // -----------------------------------------------------------------------

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // -----------------------------------------------------------------------
  // Keyboard navigation
  // -----------------------------------------------------------------------

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
      return;
    }

    if (e.key === 'Enter' && activeIndex >= 0 && activeIndex < results.length) {
      e.preventDefault();
      selectResult(results[activeIndex]);
    }
  };

  // -----------------------------------------------------------------------
  // Selection
  // -----------------------------------------------------------------------

  const selectResult = (result: SearchResult) => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    setActiveIndex(-1);
    navigate(result.route);
  };

  // -----------------------------------------------------------------------
  // Group results by category
  // -----------------------------------------------------------------------

  const grouped = CATEGORY_ORDER
    .map((cat) => ({
      category: cat,
      items: results.filter((r) => r.category === cat),
    }))
    .filter((g) => g.items.length > 0);

  // Build flat index mapping for keyboard nav
  let flatIndex = 0;
  const indexMap = new Map<string, number>();
  for (const group of grouped) {
    for (const item of group.items) {
      indexMap.set(item.id, flatIndex++);
    }
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const showDropdown = isOpen && (isLoading || results.length > 0 || query.length >= 2);

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Search input */}
      <div className="relative">
        <label htmlFor="global-search" className="sr-only">Search guests, knowledge base, sessions</label>
        <Input
          ref={inputRef}
          id="global-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={propertyId ? 'Search guests, knowledge base, sessions...' : 'Select a property to search...'}
          disabled={!propertyId}
          autoComplete="off"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="global-search-results"
          aria-activedescendant={activeIndex >= 0 ? results[activeIndex]?.id : undefined}
          leftIcon={<Search className="w-5 h-5" aria-hidden="true" />}
          className="[&_input]:pr-16"
        />
        {isLoading ? (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-surface-400 animate-spin" />
        ) : (
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-surface-400 bg-surface-100 px-1.5 py-0.5 rounded border border-surface-200 pointer-events-none select-none">
            {typeof navigator !== 'undefined' && navigator.platform?.includes('Mac') ? '\u2318K' : 'Ctrl+K'}
          </span>
        )}
      </div>

      {/* Results dropdown */}
      {showDropdown && (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-surface-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          {isLoading && results.length === 0 && (
            <div className="px-4 py-6 text-center text-surface-500 text-sm">
              Searching...
            </div>
          )}

          {!isLoading && results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-6 text-center text-surface-500 text-sm">
              No results for &ldquo;{query}&rdquo;
            </div>
          )}

          {grouped.map((group) => {
            const { label, Icon } = CATEGORY_CONFIG[group.category];
            return (
              <div key={group.category}>
                <div className="px-3 py-2 text-xs font-semibold text-surface-500 uppercase tracking-wider flex items-center gap-1.5 bg-surface-50 border-b border-surface-100">
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </div>
                {group.items.map((item) => {
                  const idx = indexMap.get(item.id) ?? -1;
                  const isActive = idx === activeIndex;
                  return (
                    <Button
                      key={item.id}
                      id={item.id}
                      role="option"
                      aria-selected={isActive}
                      type="button"
                      variant="ghost"
                      className={`w-full !justify-start !block text-left px-4 py-2.5 transition-colors !text-current font-normal ${
                        isActive ? '!bg-primary-50 !text-primary-900' : 'hover:!bg-surface-50'
                      }`}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectResult(item);
                      }}
                    >
                      <span className="text-sm font-medium text-surface-900">{item.label}</span>
                      {item.sublabel && (
                        <span className="text-xs text-surface-500 mt-0.5 block">{item.sublabel}</span>
                      )}
                    </Button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
